"use client"
import { useState, useCallback } from "react"
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  alpha,
  Alert,
} from "@mui/material"
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  PictureAsPdf as PictureAsPdfIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from "@mui/icons-material"

const FileUploadDragDrop = ({ files, onFilesChange, maxFiles = 5, maxSizeMB = 10, maxTotalSizeMB = null }) => {
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState(null)

  // Validar tipo de archivo
  const isValidFileType = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
    return validTypes.includes(file.type)
  }

  // Convertir archivo a Base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        // Remover el prefijo "data:image/jpeg;base64," o similar
        const base64String = reader.result.split(',')[1]
        resolve({
          nombre: file.name,
          tipo: file.type,
          tamaño: file.size,
          base64: base64String,
        })
      }
      reader.onerror = (error) => reject(error)
    })
  }

  // Procesar archivos
  const processFiles = async (fileList) => {
    const newFiles = Array.from(fileList)
    
    // Validar cantidad
    if (files.length + newFiles.length > maxFiles) {
      setError(`Solo se pueden subir máximo ${maxFiles} archivos`)
      setTimeout(() => setError(null), 5000)
      return
    }

    // Calcular tamaño total de archivos existentes
    const existingTotalSize = files.reduce((total, file) => total + (file.tamaño || 0), 0)

    // Validar tipos y tamaños
    const validFiles = []
    let accumulatedSize = 0
    
    for (const file of newFiles) {
      if (!isValidFileType(file)) {
        setError(`El archivo ${file.name} no es válido. Solo se permiten JPG, PNG o PDF`)
        setTimeout(() => setError(null), 5000)
        continue
      }

      // Validar tamaño individual si maxSizeMB está definido
      if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
        setError(`El archivo ${file.name} excede el tamaño máximo de ${maxSizeMB}MB`)
        setTimeout(() => setError(null), 5000)
        continue
      }

      // Validar tamaño total si maxTotalSizeMB está definido
      if (maxTotalSizeMB) {
        const newTotalSize = existingTotalSize + accumulatedSize + file.size
        if (newTotalSize > maxTotalSizeMB * 1024 * 1024) {
          setError(`El tamaño total de los archivos no puede exceder ${maxTotalSizeMB}MB. Tamaño actual: ${(existingTotalSize / (1024 * 1024)).toFixed(2)}MB, nuevo archivo: ${(file.size / (1024 * 1024)).toFixed(2)}MB`)
          setTimeout(() => setError(null), 5000)
          continue
        }
        accumulatedSize += file.size
      }

      validFiles.push(file)
    }

    if (validFiles.length === 0) return

    // Convertir a Base64
    try {
      const base64Files = await Promise.all(validFiles.map(fileToBase64))
      const updatedFiles = [...files, ...base64Files]
      onFilesChange(updatedFiles)
    } catch (error) {
      console.error("Error al convertir archivos:", error)
      setError("Error al procesar los archivos. Intente nuevamente.")
      setTimeout(() => setError(null), 5000)
    }
  }

  // Manejar drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }, [])

  // Manejar drop
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      setDragActive(false)

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processFiles(e.dataTransfer.files)
      }
    },
    [files]
  )

  // Manejar input file
  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files)
      // Reset input para permitir seleccionar el mismo archivo nuevamente
      e.target.value = ""
    }
  }

  // Eliminar archivo
  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index)
    onFilesChange(updatedFiles)
  }

  // Obtener icono según tipo
  const getFileIcon = (tipo) => {
    if (tipo.startsWith('image/')) {
      return <ImageIcon fontSize="small" />
    } else if (tipo === 'application/pdf') {
      return <PictureAsPdfIcon fontSize="small" />
    }
    return <InsertDriveFileIcon fontSize="small" />
  }

  // Formatear tamaño
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
  }

  return (
    <Box>
      {/* Zona de drag & drop */}
      <Paper
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        elevation={dragActive ? 8 : 2}
        sx={{
          p: 3,
          border: `2px dashed ${dragActive ? "rgb(244, 177, 14)" : "#ccc"}`,
          borderRadius: 2,
          backgroundColor: dragActive ? alpha("rgb(244, 177, 14)", 0.05) : "white",
          transition: "all 0.3s ease",
          cursor: "pointer",
          textAlign: "center",
          "&:hover": {
            borderColor: "rgb(244, 177, 14)",
            backgroundColor: alpha("rgb(244, 177, 14)", 0.05),
          },
        }}
        onClick={() => document.getElementById("file-upload-input").click()}
      >
        <input
          id="file-upload-input"
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,application/pdf"
          onChange={handleInputChange}
          style={{ display: "none" }}
        />
        <CloudUploadIcon
          sx={{
            fontSize: 48,
            color: dragActive ? "rgb(244, 177, 14)" : "#ccc",
            mb: 1,
          }}
        />
        <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
          Arrastra archivos aquí o haz clic para seleccionar
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Formatos permitidos: JPG, PNG, PDF
          {maxTotalSizeMB 
            ? ` (Máx. ${maxTotalSizeMB}MB total entre ${maxFiles} archivos)`
            : maxSizeMB 
            ? ` (Máx. ${maxSizeMB}MB por archivo, ${maxFiles} archivos máximo)`
            : ` (${maxFiles} archivos máximo)`}
        </Typography>
      </Paper>

      {/* Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Lista de archivos */}
      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Archivos adjuntos ({files.length}/{maxFiles}):
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {files.map((file, index) => (
              <Chip
                key={index}
                icon={getFileIcon(file.tipo)}
                label={`${file.nombre} (${formatFileSize(file.tamaño)})`}
                onDelete={() => handleRemoveFile(index)}
                color="primary"
                variant="outlined"
                sx={{
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default FileUploadDragDrop

